// @ts-ignore
import { hasProp, assert } from "@utils/assertion";
// @ts-ignore
import { isArray } from "@utils/arrayUtils";
// @ts-ignore
import { parseJsonWithNumbersAsBigint, renderJsonWithNumbersAsBigint, type ParserResult } from "@utils/json";
// @ts-ignore
import { toBase64 } from "@utils/blobUtils";
// @ts-ignore
import { DateTime } from "luxon";
// @ts-ignore
import { type ReadonlyUint8Array } from "@utils/arrayUtils";
import type { ContentId } from "../schema";

export function serialize_ContentId_internal(value: ContentId): unknown {
  return ((
    value:
      | { contentType: "SUMMARY"; id: bigint }
      | { contentType: "ACTIONABLE"; id: bigint }
      | { contentType: "MIAB"; id: bigint }
      | { contentType: "EXPERT_VIDEO"; id: bigint }
      | { contentType: "ONLINE_CONTENT"; id: bigint }
      | { contentType: "QUICKWIN"; id: bigint },
  ): unknown => {
    switch (value.contentType) {
      case "SUMMARY": {
        return {
          contentType: "SUMMARY",
          id: ((value: bigint): unknown => {
            return value;
          })(value["id"]),
        };
      }
      case "ACTIONABLE": {
        return {
          contentType: "ACTIONABLE",
          id: ((value: bigint): unknown => {
            return value;
          })(value["id"]),
        };
      }
      case "MIAB": {
        return {
          contentType: "MIAB",
          id: ((value: bigint): unknown => {
            return value;
          })(value["id"]),
        };
      }
      case "EXPERT_VIDEO": {
        return {
          contentType: "EXPERT_VIDEO",
          id: ((value: bigint): unknown => {
            return value;
          })(value["id"]),
        };
      }
      case "ONLINE_CONTENT": {
        return {
          contentType: "ONLINE_CONTENT",
          id: ((value: bigint): unknown => {
            return value;
          })(value["id"]),
        };
      }
      case "QUICKWIN": {
        return {
          contentType: "QUICKWIN",
          id: ((value: bigint): unknown => {
            return value;
          })(value["id"]),
        };
      }
    }
  })(value);
}
export function serialize_ContentId(obj: ContentId): string {
  return renderJsonWithNumbersAsBigint(serialize_ContentId_internal(obj));
}
export function deserialize_ContentId_internal(value: unknown): ParserResult<ContentId> {
  return ((
    value: unknown,
  ): ParserResult<
    | { contentType: "SUMMARY"; id: bigint }
    | { contentType: "ACTIONABLE"; id: bigint }
    | { contentType: "MIAB"; id: bigint }
    | { contentType: "EXPERT_VIDEO"; id: bigint }
    | { contentType: "ONLINE_CONTENT"; id: bigint }
    | { contentType: "QUICKWIN"; id: bigint }
  > => {
    return ((
      value: unknown,
    ): ParserResult<
      | { contentType: "SUMMARY"; id: bigint }
      | { contentType: "ACTIONABLE"; id: bigint }
      | { contentType: "MIAB"; id: bigint }
      | { contentType: "EXPERT_VIDEO"; id: bigint }
      | { contentType: "ONLINE_CONTENT"; id: bigint }
      | { contentType: "QUICKWIN"; id: bigint }
    > => {
      if (value == null) {
        return { success: false, reason: `[#/components/schemas/ContentId]: '${value}' expected to be non-null.` };
      }
      if (typeof value != "object") {
        return { success: false, reason: `[#/components/schemas/ContentId]: ${value} expected to be of type 'object'.` };
      }
      if (!hasProp(value, "contentType")) {
        return { success: false, reason: `[#/components/schemas/ContentId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'contentType'.` };
      }
      if (typeof value["contentType"] != "string") {
        return { success: false, reason: `[#/components/schemas/ContentId]: '${value["contentType"]}' expected to be of type 'string'.` };
      }
      const discriminator = value["contentType"];
      switch (discriminator) {
        case "SUMMARY": {
          const parsedVariant = ((
            value: unknown,
          ): ParserResult<{
            id: bigint;
          }> => {
            if (typeof value !== "object") {
              return { success: false, reason: `[#/components/schemas/ContentId/SUMMARY]: '${value}' expected to be of type 'object'.` };
            }
            if (value === null) {
              return { success: false, reason: `[#/components/schemas/ContentId/SUMMARY]: '${value}' expected to not be null.` };
            }
            if (!hasProp(value, "id")) {
              return { success: false, reason: `[#/components/schemas/ContentId/SUMMARY/id]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'id'.` };
            }
            const parsed_id = ((): ParserResult<bigint> => {
              return ((value: unknown): ParserResult<bigint> => {
                if (typeof value === "number") {
                  return { success: true, value: BigInt(value) };
                }
                if (typeof value === "bigint") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentId/SUMMARY/id]: '${value}' expected to be of type bigint or number.` };
              })(value["id"]);
            })();
            if (!parsed_id.success) {
              return { success: false, reason: parsed_id.reason };
            }
            return { success: true, value: { id: parsed_id.value } };
          })(value);
          if (!parsedVariant.success) {
            return { success: false, reason: parsedVariant.reason };
          }
          return { success: true, value: { contentType: "SUMMARY" as const, ...parsedVariant.value } };
        }
        case "ACTIONABLE": {
          const parsedVariant = ((
            value: unknown,
          ): ParserResult<{
            id: bigint;
          }> => {
            if (typeof value !== "object") {
              return { success: false, reason: `[#/components/schemas/ContentId/ACTIONABLE]: '${value}' expected to be of type 'object'.` };
            }
            if (value === null) {
              return { success: false, reason: `[#/components/schemas/ContentId/ACTIONABLE]: '${value}' expected to not be null.` };
            }
            if (!hasProp(value, "id")) {
              return { success: false, reason: `[#/components/schemas/ContentId/ACTIONABLE/id]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'id'.` };
            }
            const parsed_id = ((): ParserResult<bigint> => {
              return ((value: unknown): ParserResult<bigint> => {
                if (typeof value === "number") {
                  return { success: true, value: BigInt(value) };
                }
                if (typeof value === "bigint") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentId/ACTIONABLE/id]: '${value}' expected to be of type bigint or number.` };
              })(value["id"]);
            })();
            if (!parsed_id.success) {
              return { success: false, reason: parsed_id.reason };
            }
            return { success: true, value: { id: parsed_id.value } };
          })(value);
          if (!parsedVariant.success) {
            return { success: false, reason: parsedVariant.reason };
          }
          return { success: true, value: { contentType: "ACTIONABLE" as const, ...parsedVariant.value } };
        }
        case "MIAB": {
          const parsedVariant = ((
            value: unknown,
          ): ParserResult<{
            id: bigint;
          }> => {
            if (typeof value !== "object") {
              return { success: false, reason: `[#/components/schemas/ContentId/MIAB]: '${value}' expected to be of type 'object'.` };
            }
            if (value === null) {
              return { success: false, reason: `[#/components/schemas/ContentId/MIAB]: '${value}' expected to not be null.` };
            }
            if (!hasProp(value, "id")) {
              return { success: false, reason: `[#/components/schemas/ContentId/MIAB/id]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'id'.` };
            }
            const parsed_id = ((): ParserResult<bigint> => {
              return ((value: unknown): ParserResult<bigint> => {
                if (typeof value === "number") {
                  return { success: true, value: BigInt(value) };
                }
                if (typeof value === "bigint") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentId/MIAB/id]: '${value}' expected to be of type bigint or number.` };
              })(value["id"]);
            })();
            if (!parsed_id.success) {
              return { success: false, reason: parsed_id.reason };
            }
            return { success: true, value: { id: parsed_id.value } };
          })(value);
          if (!parsedVariant.success) {
            return { success: false, reason: parsedVariant.reason };
          }
          return { success: true, value: { contentType: "MIAB" as const, ...parsedVariant.value } };
        }
        case "EXPERT_VIDEO": {
          const parsedVariant = ((
            value: unknown,
          ): ParserResult<{
            id: bigint;
          }> => {
            if (typeof value !== "object") {
              return { success: false, reason: `[#/components/schemas/ContentId/EXPERT_VIDEO]: '${value}' expected to be of type 'object'.` };
            }
            if (value === null) {
              return { success: false, reason: `[#/components/schemas/ContentId/EXPERT_VIDEO]: '${value}' expected to not be null.` };
            }
            if (!hasProp(value, "id")) {
              return { success: false, reason: `[#/components/schemas/ContentId/EXPERT_VIDEO/id]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'id'.` };
            }
            const parsed_id = ((): ParserResult<bigint> => {
              return ((value: unknown): ParserResult<bigint> => {
                if (typeof value === "number") {
                  return { success: true, value: BigInt(value) };
                }
                if (typeof value === "bigint") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentId/EXPERT_VIDEO/id]: '${value}' expected to be of type bigint or number.` };
              })(value["id"]);
            })();
            if (!parsed_id.success) {
              return { success: false, reason: parsed_id.reason };
            }
            return { success: true, value: { id: parsed_id.value } };
          })(value);
          if (!parsedVariant.success) {
            return { success: false, reason: parsedVariant.reason };
          }
          return { success: true, value: { contentType: "EXPERT_VIDEO" as const, ...parsedVariant.value } };
        }
        case "ONLINE_CONTENT": {
          const parsedVariant = ((
            value: unknown,
          ): ParserResult<{
            id: bigint;
          }> => {
            if (typeof value !== "object") {
              return { success: false, reason: `[#/components/schemas/ContentId/ONLINE_CONTENT]: '${value}' expected to be of type 'object'.` };
            }
            if (value === null) {
              return { success: false, reason: `[#/components/schemas/ContentId/ONLINE_CONTENT]: '${value}' expected to not be null.` };
            }
            if (!hasProp(value, "id")) {
              return { success: false, reason: `[#/components/schemas/ContentId/ONLINE_CONTENT/id]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'id'.` };
            }
            const parsed_id = ((): ParserResult<bigint> => {
              return ((value: unknown): ParserResult<bigint> => {
                if (typeof value === "number") {
                  return { success: true, value: BigInt(value) };
                }
                if (typeof value === "bigint") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentId/ONLINE_CONTENT/id]: '${value}' expected to be of type bigint or number.` };
              })(value["id"]);
            })();
            if (!parsed_id.success) {
              return { success: false, reason: parsed_id.reason };
            }
            return { success: true, value: { id: parsed_id.value } };
          })(value);
          if (!parsedVariant.success) {
            return { success: false, reason: parsedVariant.reason };
          }
          return { success: true, value: { contentType: "ONLINE_CONTENT" as const, ...parsedVariant.value } };
        }
        case "QUICKWIN": {
          const parsedVariant = ((
            value: unknown,
          ): ParserResult<{
            id: bigint;
          }> => {
            if (typeof value !== "object") {
              return { success: false, reason: `[#/components/schemas/ContentId/QUICKWIN]: '${value}' expected to be of type 'object'.` };
            }
            if (value === null) {
              return { success: false, reason: `[#/components/schemas/ContentId/QUICKWIN]: '${value}' expected to not be null.` };
            }
            if (!hasProp(value, "id")) {
              return { success: false, reason: `[#/components/schemas/ContentId/QUICKWIN/id]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'id'.` };
            }
            const parsed_id = ((): ParserResult<bigint> => {
              return ((value: unknown): ParserResult<bigint> => {
                if (typeof value === "number") {
                  return { success: true, value: BigInt(value) };
                }
                if (typeof value === "bigint") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentId/QUICKWIN/id]: '${value}' expected to be of type bigint or number.` };
              })(value["id"]);
            })();
            if (!parsed_id.success) {
              return { success: false, reason: parsed_id.reason };
            }
            return { success: true, value: { id: parsed_id.value } };
          })(value);
          if (!parsedVariant.success) {
            return { success: false, reason: parsedVariant.reason };
          }
          return { success: true, value: { contentType: "QUICKWIN" as const, ...parsedVariant.value } };
        }
        default:
          return { success: false, reason: `[#/components/schemas/ContentId]: '${discriminator}' expected to be one of 'SUMMARY, ACTIONABLE, MIAB, EXPERT_VIDEO, ONLINE_CONTENT, QUICKWIN'.` };
      }
    })(value);
  })(value);
}
export function deserialize_ContentId(str: string): ParserResult<ContentId> {
  return deserialize_ContentId_internal(parseJsonWithNumbersAsBigint(str));
}
